import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { Link, useLocation, useHistory } from "react-router-dom";
import { useTheme, useMediaQuery, IconButton, Menu, MenuItem } from "@mui/material";

import Icon from "../Icon/Icon";
import NotificationButton from "../Notification/NotificationButton/NotificationButton";
import NewPostButton from "../NewPost/NewPostButton/NewPostButton";

const MobileNav = ({ currentUser }) => {
  const { pathname } = useLocation();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isImmersive = isMobile && pathname === "/";
  const [overflowAnchorEl, setOverflowAnchorEl] = useState(null);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [menuResetKey, setMenuResetKey] = useState(0);
  const overflowButtonRef = useRef(null);

  const navClassName = classNames("mobile-nav", {
    "mobile-nav--dark": isImmersive,
  });

  const overflowItems = useMemo(() => {
    if (!currentUser) return [];
    const items = [];
    const normalizedType = (currentUser.userType || "").toUpperCase();
    if (normalizedType === "ATHLETE") {
      items.push({
        path: "/creator/subscriptions",
        label: "Creator studio",
        icon: "podium",
      });
    }
    items.push({
      path: "/subscriptions",
      label: "My subscriptions",
      icon: "wallet",
    });
    items.push({
      path: currentUser.username ? `/${currentUser.username}` : "/",
      label: "My profile",
      icon: "person-circle",
      isProfile: true,
    });
    if (currentUser.roles?.includes("SuperAdmin")) {
      items.push({
        path: "/super-admin",
        label: "Super admin",
        icon: "shield-checkmark",
      });
    }
    return items;
  }, [currentUser]);

  const showOverflowMenu = isMobile && overflowItems.length > 0;

  const handleOverflowOpen = () => {
    if (!isMobile) return;
    setOverflowAnchorEl(overflowButtonRef.current);
    setOverflowOpen(true);
  };

  const handleOverflowClose = useCallback(() => {
    setOverflowAnchorEl(null);
    setOverflowOpen(false);
  }, []);

  const handleMenuNavigate = (item) => () => {
    const targetPath = item.isProfile
      ? `/${currentUser?.username}`
      : item.path;
    // Close first (state) then navigate to guarantee backdrop disappears
    handleOverflowClose();
    if (targetPath && pathname !== targetPath) history.push(targetPath);
    else history.replace(targetPath);
  };

  useEffect(() => {
    handleOverflowClose();
    setMenuResetKey((k) => k + 1); // force Menu remount after route change
  }, [pathname, handleOverflowClose]);

  useEffect(() => {
    const unlisten = history.listen(() => handleOverflowClose());
    return unlisten;
  }, [history, handleOverflowClose]);

  useEffect(() => {
    if (!isMobile && overflowOpen) handleOverflowClose();
  }, [isMobile, overflowOpen, handleOverflowClose]);

  return (
    <nav className={navClassName}>
      <ul className="mobile-nav__list">
        <li>
          <Link to="/">
            <Icon icon={pathname === "/" ? "home" : "home-outline"} />
          </Link>
        </li>
        <li>
          <Link to="/explore">
            <Icon
              icon={pathname === "/explore" ? "search" : "search-outline"}
            />
          </Link>
        </li>
        {(currentUser?.userType || '').toUpperCase() === 'ATHLETE' && (
          <li>
            <NewPostButton plusIcon />
          </li>
        )}
        <li>
          <Link to="/activity">
            <Icon
              icon={pathname === "/activity" ? "heart" : "heart-outline"}
            />
          </Link>
        </li>
        <li>
          <NotificationButton mobile />
        </li>
        {showOverflowMenu && (
          <li>
            <IconButton
              size="small"
              onClick={handleOverflowOpen}
              ref={overflowButtonRef}
            >
              <Icon icon="menu-outline" />
            </IconButton>
            <Menu
              key={menuResetKey}
              anchorEl={overflowAnchorEl || overflowButtonRef.current}
              open={overflowOpen}
              onClose={handleOverflowClose}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              transformOrigin={{ vertical: "bottom", horizontal: "center" }}
              keepMounted={false}
              transitionDuration={0}
              MenuListProps={{ onClick: handleOverflowClose }}
            >
              {overflowItems.map((item) => (
                <MenuItem
                  key={item.label}
                  onClick={handleMenuNavigate(item)}
                >
                  <Icon icon={item.icon} style={{ marginRight: "0.75rem" }} />
                  {item.isProfile ? currentUser.username : item.label}
                </MenuItem>
              ))}
            </Menu>
          </li>
        )}
      </ul>
    </nav>
  );
};

export default MobileNav;

